/*
  LFDL - Upsell Checkpoints (sans serveur)
  - Checkpoint 1: avant ouverture panier (/cart)
  - Checkpoint 2: avant passage au paiement (/checkout)
  - Sélection "logique": produits complémentaires (Search & Discovery) sur l'article le plus cher,
    puis fallback collections (handles) si vide.
  - Anti-spam: 1 fois par session OU seulement si le panier a changé (signature items+qty).
*/

(() => {
  "use strict";

  const DEFAULT_CONFIG = {
    enabled: true,
    limitMin: 2,
    limitMax: 4,
    fallbackCollectionCheckpoint1: "upsell-entree-panier",
    fallbackCollectionCheckpoint2: "upsell-checkout",
    titleCheckpoint1: "Avant de voir votre panier, vous n’avez pas oublié ça ?",
    titleCheckpoint2: "Dernière vérification avant paiement",
    ctaViewCart: "Voir mon panier",
    ctaContinue: "Continuer mes achats",
    ctaCheckoutAdd: "Ajouter et passer au paiement",
    ctaCheckoutSkip: "Passer au paiement sans ajouter",
    addLabel: "Ajouter",
    addedLabel: "Article ajouté au panier",
    toastAdded: "Article rajouté au panier",
    oncePerSignature: true
  };

  const CONFIG = Object.assign({}, DEFAULT_CONFIG, window.LFDL_UPSELL_CHECKPOINTS_CONFIG || {});

  if (!CONFIG.enabled) return;

  const STORAGE = {
    cp1Sig: "lfdl_upsell_cp1_sig",
    cp2Sig: "lfdl_upsell_cp2_sig",
    cp1Shown: "lfdl_upsell_cp1_shown_products",
    cp2Shown: "lfdl_upsell_cp2_shown_products"
  };

  const BYPASS_FLAG = "__lfdlUpsellBypass";

  let overlayEl = null;
  let toastEl = null;
  let modalOpen = false;

  function safeJsonParse(value, fallback) {
    try {
      return JSON.parse(value);
    } catch (e) {
      return fallback;
    }
  }

  function uniq(arr) {
    return Array.from(new Set(arr));
  }

  function toMoney(cents, currency) {
    const amount = (cents || 0) / 100;
    try {
      return new Intl.NumberFormat(document.documentElement.lang || "fr-FR", {
        style: "currency",
        currency: currency || "EUR"
      }).format(amount);
    } catch (e) {
      return `${amount.toFixed(2)} ${currency || "EUR"}`;
    }
  }

  async function fetchJson(url, options) {
    const res = await fetch(url, Object.assign(
      {
        headers: {
          "Accept": "application/json"
        },
        credentials: "same-origin"
      },
      options || {}
    ));

    if (!res.ok) {
      const text = await res.text().catch(() => "");
      throw new Error(`HTTP ${res.status} ${res.statusText} ${text}`);
    }

    return res.json();
  }

  async function getCart() {
    return fetchJson("/cart.js");
  }

  function cartSignature(cart) {
    const parts = (cart.items || [])
      .map((it) => `${it.variant_id}:${it.quantity}`)
      .sort();
    return parts.join("|");
  }

  function cartVariantIds(cart) {
    return new Set((cart.items || []).map((it) => String(it.variant_id)));
  }

  function cartProductIds(cart) {
    return new Set((cart.items || []).map((it) => String(it.product_id)));
  }

  function pickLeadProducts(cart) {
    const items = Array.isArray(cart.items) ? cart.items.slice() : [];
    const scored = items
      .map((it) => {
        const unit = it.quantity ? Math.round((it.final_price || it.price || 0) / it.quantity) : (it.final_price || it.price || 0);
        return {
          productId: it.product_id,
          score: unit
        };
      })
      .filter((x) => !!x.productId);

    scored.sort((a, b) => b.score - a.score);
    const ids = scored.map((x) => x.productId);
    return uniq(ids).slice(0, 2);
  }

  function normalizeRecommendedProduct(p) {
    if (!p) return null;

    const variants = Array.isArray(p.variants) ? p.variants : [];
    const firstAvail = variants.find((v) => v && v.available) || variants[0];
    if (!firstAvail || !firstAvail.id) return null;

    const image = (p.featured_image && p.featured_image.url) || (p.images && p.images[0]) || "";
    const url = p.url || (p.handle ? `/products/${p.handle}` : "#");

    return {
      productId: String(p.id),
      variantId: String(firstAvail.id),
      title: p.title || "",
      url,
      image,
      price: typeof firstAvail.price === "number" ? firstAvail.price : (p.price || 0),
      currency: document.documentElement.getAttribute("data-shopify-currency") || "EUR"
    };
  }

  async function fetchComplementaryProducts(productId, limit) {
    const urls = [
      `/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}&intent=complementary`,
      `/recommendations/products.json?product_id=${encodeURIComponent(productId)}&limit=${encodeURIComponent(limit)}`
    ];

    for (const url of urls) {
      try {
        const data = await fetchJson(url);
        const products = Array.isArray(data.products) ? data.products : [];
        return products.map(normalizeRecommendedProduct).filter(Boolean);
      } catch (e) {
        // try next
      }
    }

    return [];
  }

  async function fetchCollectionProducts(handle, limit) {
    if (!handle) return [];

    const fetchFromHandle = async (h) => {
      try {
        const data = await fetchJson(`/collections/${encodeURIComponent(h)}/products.json?limit=50`);
        const products = Array.isArray(data.products) ? data.products : [];
        return products
          .map((p) => {
            const variants = Array.isArray(p.variants) ? p.variants : [];
            const firstAvail = variants.find((v) => v && v.available) || variants[0];
            if (!firstAvail || !firstAvail.id) return null;
            const image = (p.images && p.images[0] && p.images[0].src) || (p.image && p.image.src) || "";
            return {
              productId: String(p.id),
              variantId: String(firstAvail.id),
              title: p.title || "",
              url: p.handle ? `/products/${p.handle}` : "#",
              image,
              price: typeof firstAvail.price === "string" ? Math.round(parseFloat(firstAvail.price) * 100) : (firstAvail.price || 0),
              currency: document.documentElement.getAttribute("data-shopify-currency") || "EUR"
            };
          })
          .filter(Boolean);
      } catch (e) {
        return [];
      }
    };

    // 1) Collection configurée (ex: upsell-entree-panier / upsell-checkout)
    let out = await fetchFromHandle(handle);

    // 2) Si vide (collection vide, ou handle incorrect), fallback "all" pour ne jamais être à sec
    if ((!out || out.length === 0) && String(handle) !== "all") {
      out = await fetchFromHandle("all");
    }

    return (out || []).slice(0, Math.max(1, limit || 4));
  }


  function filterUpsell(products, cart, excludeProductIds, max) {
    const inCartVariants = cartVariantIds(cart);
    const inCartProducts = cartProductIds(cart);
    const exclude = new Set((excludeProductIds || []).map(String));

    const out = [];
    for (const p of products) {
      if (!p) continue;
      if (!p.variantId || !p.productId) continue;
      if (inCartVariants.has(String(p.variantId))) continue;
      if (inCartProducts.has(String(p.productId))) continue;
      if (exclude.has(String(p.productId))) continue;
      if (out.find((x) => x.productId === p.productId)) continue;
      out.push(p);
      if (out.length >= max) break;
    }
    return out;
  }

  function ensureOverlay() {
    if (overlayEl) return overlayEl;

    overlayEl = document.createElement("div");
    overlayEl.className = "lfdl-upsell-overlay";
    overlayEl.innerHTML = `
      <div class="lfdl-upsell-modal" role="dialog" aria-modal="true" aria-label="Upsell">
        <div class="lfdl-upsell-modal__head">
          <div>
            <h2 class="lfdl-upsell-modal__title"></h2>
            <p class="lfdl-upsell-modal__sub"></p>
          </div>
          <button type="button" class="lfdl-upsell-modal__close" aria-label="Fermer">×</button>
        </div>
        <div class="lfdl-upsell-modal__body">
          <div class="lfdl-upsell-grid"></div>
          <div class="lfdl-upsell-actions"></div>
        </div>
      </div>
    `;

    document.body.appendChild(overlayEl);

    overlayEl.addEventListener("click", (e) => {
      if (e.target === overlayEl) closeModal();
    });

    overlayEl.querySelector(".lfdl-upsell-modal__close").addEventListener("click", () => closeModal());

    document.addEventListener("keydown", (e) => {
      if (!modalOpen) return;
      if (e.key === "Escape") closeModal();
    });

    return overlayEl;
  }

  function ensureToast() {
    if (toastEl) return toastEl;
    toastEl = document.createElement("div");
    toastEl.className = "lfdl-upsell-toast";
    toastEl.textContent = CONFIG.toastAdded;
    document.body.appendChild(toastEl);
    return toastEl;
  }

  let toastTimer = null;
  function showToast(message) {
    const t = ensureToast();
    t.textContent = message || CONFIG.toastAdded;
    t.classList.add("is-on");
    clearTimeout(toastTimer);
    toastTimer = setTimeout(() => t.classList.remove("is-on"), 1600);
  }

  function closeModal() {
    if (!overlayEl) return;
    overlayEl.classList.remove("is-open");
    modalOpen = false;
  }

  function openModal({
    title,
    subtitle,
    products,
    mode,
    onPrimary,
    onSecondary,
    primaryLabel,
    secondaryLabel
  }) {
    const overlay = ensureOverlay();
    const titleEl = overlay.querySelector(".lfdl-upsell-modal__title");
    const subEl = overlay.querySelector(".lfdl-upsell-modal__sub");
    const gridEl = overlay.querySelector(".lfdl-upsell-grid");
    const actionsEl = overlay.querySelector(".lfdl-upsell-actions");

    titleEl.textContent = title || "";
    subEl.textContent = subtitle || "";

    gridEl.innerHTML = "";
    actionsEl.innerHTML = "";

    const selected = new Set();
    const added = new Set();

    const createCard = (p) => {
      const card = document.createElement("div");
      card.className = "lfdl-upsell-card";
      card.dataset.productId = p.productId;
      card.dataset.variantId = p.variantId;

      const priceTxt = typeof p.price === "number" ? toMoney(p.price, p.currency) : "";

      card.innerHTML = `
        <div class="lfdl-upsell-card__media">${p.image ? `<img src="${p.image}" alt="">` : ""}</div>
        <div class="lfdl-upsell-card__content">
          <h3 class="lfdl-upsell-card__title"><a href="${p.url}">${escapeHtml(p.title)}</a></h3>
          <div class="lfdl-upsell-card__meta">
            <div class="lfdl-upsell-card__price">${priceTxt}</div>
            ${mode === "checkout" ? `
              <label class="lfdl-upsell-card__select">
                <input type="checkbox" data-action="select">
                <span>Sélectionner</span>
              </label>
            ` : ""}
          </div>
          <div style="display:flex; gap:10px; align-items:center; justify-content:flex-end;">
            <button type="button" class="lfdl-upsell-btn" data-action="add">${CONFIG.addLabel}</button>
          </div>
        </div>
      `;

      const addBtn = card.querySelector('[data-action="add"]');
      const selectInput = card.querySelector('[data-action="select"]');

      if (selectInput) {
        selectInput.addEventListener("click", (e) => {
          e.stopPropagation();
        });

        selectInput.addEventListener("change", () => {
          if (selectInput.checked) {
            selected.add(p.variantId);
          } else {
            selected.delete(p.variantId);
          }
        });
      }

      addBtn.addEventListener("click", async () => {
        if (addBtn.classList.contains("is-added")) return;
        addBtn.classList.add("is-loading");
        try {
          await addItemsToCart([{ id: p.variantId, quantity: 1 }]);
          // Anti-spam: si le panier a changé à cause de l'upsell lui-même,
          // on met à jour la signature stockée du checkpoint courant.
          try {
            const nextCart = await getCart();
            const nextSig = cartSignature(nextCart);
            if (mode === "cart") sessionStorage.setItem(STORAGE.cp1Sig, nextSig);
            if (mode === "checkout") sessionStorage.setItem(STORAGE.cp2Sig, nextSig);
          } catch (e) {
            // ignore
          }
          addBtn.classList.remove("is-loading");
          addBtn.classList.add("is-added");
          addBtn.textContent = CONFIG.addedLabel;
          added.add(p.variantId);
          if (selectInput) {
            selectInput.checked = true;
            selected.add(p.variantId);
          }
          showToast(CONFIG.toastAdded);
        } catch (err) {
          addBtn.classList.remove("is-loading");
          showToast("Erreur d’ajout au panier");
        }
      });

      return card;
    };

    (products || []).forEach((p) => gridEl.appendChild(createCard(p)));

    const primaryBtn = document.createElement("button");
    primaryBtn.type = "button";
    primaryBtn.className = "lfdl-upsell-btn";
    primaryBtn.textContent = primaryLabel || "OK";

    const secondaryBtn = document.createElement("button");
    secondaryBtn.type = "button";
    secondaryBtn.className = "lfdl-upsell-btn lfdl-upsell-btn--ghost";
    secondaryBtn.textContent = secondaryLabel || "Annuler";

    actionsEl.appendChild(secondaryBtn);
    actionsEl.appendChild(primaryBtn);

    secondaryBtn.addEventListener("click", () => {
      closeModal();
      if (typeof onSecondary === "function") onSecondary();
    });

    primaryBtn.addEventListener("click", async () => {
      if (primaryBtn.classList.contains("is-loading")) return;
      primaryBtn.classList.add("is-loading");

      try {
        if (mode === "checkout") {
          const toAdd = Array.from(selected)
            .filter((id) => id && !added.has(id))
            .map((id) => ({ id, quantity: 1 }));
          if (toAdd.length) {
            await addItemsToCart(toAdd);
            try {
              const nextCart = await getCart();
              const nextSig = cartSignature(nextCart);
              sessionStorage.setItem(STORAGE.cp2Sig, nextSig);
            } catch (e) {
              // ignore
            }
          }
        }

        primaryBtn.classList.remove("is-loading");
        closeModal();
        if (typeof onPrimary === "function") onPrimary();
      } catch (err) {
        primaryBtn.classList.remove("is-loading");
        showToast("Erreur d’ajout au panier");
      }
    });

    overlay.classList.add("is-open");
    modalOpen = true;
  }

  function escapeHtml(str) {
    return String(str || "")
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/\"/g, "&quot;")
      .replace(/'/g, "&#039;");
  }

  async function addItemsToCart(items) {
    if (!Array.isArray(items) || !items.length) return;
    await fetchJson("/cart/add.js", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Accept": "application/json"
      },
      body: JSON.stringify({ items })
    });
  }

  function sameOriginReferrerIsValidCartReturn() {
    try {
      const ref = document.referrer;
      if (!ref) return false;
      const u = new URL(ref);
      if (u.origin !== window.location.origin) return false;
      if (u.pathname === "/cart" || u.pathname.startsWith("/checkout")) return false;
      return true;
    } catch (e) {
      return false;
    }
  }

  
  async function buildProductsForCheckpoint({ cart, leadProductId, excludeProductIds, max }) {
    if (!leadProductId) return [];

    // Objectif: zéro "collection obligatoire".
    // On tente d’abord les complementary products (Search & Discovery),
    // puis fallback sur des recommandations "related" si vide.
    const cartProductIds = new Set((cart?.items || []).map((it) => Number(it.product_id)));

    async function fetchRecs(intent) {
      const url = `/recommendations/products.json?product_id=${encodeURIComponent(
        String(leadProductId)
      )}&limit=${encodeURIComponent(String(Math.max(10, max * 4)))}&intent=${encodeURIComponent(intent)}`;
      const data = await fetchJson(url);
      const products = (data && Array.isArray(data.products)) ? data.products : [];
      return products;
    }

    let products = [];
    try {
      products = await fetchRecs("complementary");
    } catch (_) {
      products = [];
    }

    if (!products.length) {
      try {
        products = await fetchRecs("related");
      } catch (_) {
        products = [];
      }
    }

    const out = [];
    for (const p of products) {
      const pid = Number(p.id);
      if (!pid) continue;
      if (excludeProductIds && excludeProductIds.has(pid)) continue;
      if (cartProductIds.has(pid)) continue;
      // Evite produits indisponibles si variants vides
      if (!p.variants || !p.variants.length) continue;
      out.push(p);
      if (out.length >= max) break;
    }

    return out;
  }
async function maybeShowCheckpoint1(proceed) {
    if (modalOpen) return;

    const cart = await getCart().catch(() => null);
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      proceed();
      return;
    }

    const sig = cartSignature(cart);
    const prevSig = sessionStorage.getItem(STORAGE.cp1Sig) || "";
    if (CONFIG.oncePerSignature && prevSig === sig) {
      proceed();
      return;
    }

    const lead = pickLeadProducts(cart)[0];
    if (!lead) {
      proceed();
      return;
    }

    const products = await buildProductsForCheckpoint({
      cart,
      leadProductId: lead,
      fallbackCollectionHandle: CONFIG.fallbackCollectionCheckpoint1,
      excludeProductIds: [],
      max: CONFIG.limitMax
    });

    if (!products.length) {
      proceed();
      return;
    }

    sessionStorage.setItem(STORAGE.cp1Sig, sig);
    sessionStorage.setItem(STORAGE.cp1Shown, JSON.stringify(products.map((p) => p.productId)));

    openModal({
      title: CONFIG.titleCheckpoint1,
      subtitle: "Ajoutez si besoin, puis continuez.",
      products,
      mode: "cart",
      primaryLabel: CONFIG.ctaViewCart,
      secondaryLabel: CONFIG.ctaContinue,
      onPrimary: proceed,
      onSecondary: () => {
        // rester sur la page
      }
    });
  }

  async function maybeShowCheckpoint2(proceedToCheckout) {
    if (modalOpen) return;

    const cart = await getCart().catch(() => null);
    if (!cart || !Array.isArray(cart.items) || cart.items.length === 0) {
      proceedToCheckout();
      return;
    }

    const sig = cartSignature(cart);
    const prevSig = sessionStorage.getItem(STORAGE.cp2Sig) || "";
    if (CONFIG.oncePerSignature && prevSig === sig) {
      proceedToCheckout();
      return;
    }

    const leads = pickLeadProducts(cart);
    const lead = leads[1] || leads[0];
    if (!lead) {
      proceedToCheckout();
      return;
    }

    const alreadyShownCp1 = safeJsonParse(sessionStorage.getItem(STORAGE.cp1Shown) || "[]", []);
    const excludeIds = Array.isArray(alreadyShownCp1) ? alreadyShownCp1 : [];

    const products = await buildProductsForCheckpoint({
      cart,
      leadProductId: lead,
      fallbackCollectionHandle: CONFIG.fallbackCollectionCheckpoint2,
      excludeProductIds: excludeIds,
      max: CONFIG.limitMax
    });

    if (!products.length) {
      proceedToCheckout();
      return;
    }

    sessionStorage.setItem(STORAGE.cp2Sig, sig);
    sessionStorage.setItem(STORAGE.cp2Shown, JSON.stringify(products.map((p) => p.productId)));

    openModal({
      title: CONFIG.titleCheckpoint2,
      subtitle: "Sélectionnez, ajoutez, puis finalisez.",
      products,
      mode: "checkout",
      primaryLabel: CONFIG.ctaCheckoutAdd,
      secondaryLabel: CONFIG.ctaCheckoutSkip,
      onPrimary: proceedToCheckout,
      onSecondary: proceedToCheckout
    });
  }

  function interceptCartOpen(e) {
    if (window[BYPASS_FLAG]) {
      window[BYPASS_FLAG] = false;
      return false;
    }

    const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
    if (!a) return false;

    let url;
    try {
      url = new URL(a.href, window.location.origin);
    } catch (err) {
      return false;
    }

    const path = url.pathname.replace(/\/+$/, "");
    if (path !== "/cart") return false;

    e.preventDefault();
    e.stopPropagation();

    const proceed = () => {
      window[BYPASS_FLAG] = true;
      // On n'essaie pas de "rejouer" un clic synthétique pour naviguer (ce n'est pas fiable).
      // On tente d'ouvrir le cart drawer si dispo, sinon on navigue vers /cart.
      try {
        const drawer = document.querySelector("cart-drawer");
        if (drawer && typeof drawer.open === "function") {
          drawer.open();
        } else {
          window.location.href = url.href;
        }
      } finally {
        setTimeout(() => {
          window[BYPASS_FLAG] = false;
        }, 250);
      }
    };

    // Sécurité anti-régression : si l'upsell plante (fetch, DOM, etc.), on ne bloque JAMAIS la navigation.
    Promise.resolve(maybeShowCheckpoint1(proceed)).catch(() => proceed());
    return true;
  }

  function interceptCheckout(e) {
    if (window[BYPASS_FLAG]) {
      window[BYPASS_FLAG] = false;
      return false;
    }

    const expressBtn = e.target && e.target.closest
      ? e.target.closest(".shopify-payment-button, .shopify-payment-button__button")
      : null;
    if (expressBtn) return false;

    const a = e.target && e.target.closest ? e.target.closest("a[href]") : null;
    if (a) {
      let url;
      try {
        url = new URL(a.href, window.location.origin);
      } catch (err) {
        url = null;
      }

      if (url) {
        const path = url.pathname.replace(/\/+$/, "");
        if (path === "/checkout") {
          e.preventDefault();
          e.stopPropagation();
          const proceed = () => {
            window[BYPASS_FLAG] = true;
            window.location.href = url.href;
            setTimeout(() => {
              window[BYPASS_FLAG] = false;
            }, 250);
          };
          // Sécurité anti-régression : si l'upsell plante (fetch, DOM, etc.), on ne bloque JAMAIS la navigation.
          Promise.resolve(maybeShowCheckpoint2(proceed)).catch(() => proceed());
          return true;
        }
      }
    }

    const btn = e.target && e.target.closest ? e.target.closest("button[name=checkout], input[name=checkout]") : null;
    if (!btn) return false;

    e.preventDefault();
    e.stopPropagation();

    const proceedBtn = () => {
      window[BYPASS_FLAG] = true;
      try {
        // IMPORTANT: form.submit() ignore le submitter => le param name="checkout" n'est pas envoyé.
        // Du coup Shopify ne redirige pas vers /checkout. Ici on force un submit "avec submitter".
        if (btn && btn.tagName === "A" && btn.href) {
          window.location.href = btn.href;
          return;
        }

        const form = (btn && btn.form) ? btn.form : (btn && btn.closest ? btn.closest("form") : null);

        if (form) {
          if (typeof form.requestSubmit === "function") {
            form.requestSubmit(btn);
          } else {
            // Fallback navigateur ancien: on injecte un champ hidden "checkout" puis submit()
            const hidden = document.createElement("input");
            hidden.type = "hidden";
            hidden.name = "checkout";
            hidden.value = "";
            form.appendChild(hidden);
            form.submit();
            hidden.remove();
          }
        } else {
          window.location.href = "/checkout";
        }
      } finally {
        setTimeout(() => {
          window[BYPASS_FLAG] = false;
        }, 250);
      }
    };

    // Sécurité anti-régression : si l'upsell plante (fetch, DOM, etc.), on ne bloque JAMAIS la navigation.
    Promise.resolve(maybeShowCheckpoint2(proceedBtn)).catch(() => proceedBtn());
    return true;
  }

  document.addEventListener(
    "click",
    (e) => {
      if (modalOpen) return;
      if (interceptCheckout(e)) return;
      interceptCartOpen(e);
    },
    true
  );

  // Optionnel: sur la page panier, le lien "Continuer vos achats" peut tenter de revenir au referrer.
  document.addEventListener("DOMContentLoaded", () => {
    const el = document.querySelector(".lfdl-cart__continue[data-lfdl-continue=auto]");
    if (!el) return;
    if (sameOriginReferrerIsValidCartReturn()) {
      el.setAttribute("href", document.referrer);
    }
  });
})();
